import { Capacitor } from '@capacitor/core';

/**
 * Everything that behaves differently inside the Android app (the same web code packaged with Capacitor).
 * On the website every helper here falls back to what the browser already does.
 */
export const isNativeApp = Capacitor.isNativePlatform();

/** The school this app was built for (set by `npm run app:brand`). Null on the website. */
export interface AppSchool { code: string; name: string; logo: string; primary: string }
const env = import.meta.env as Record<string, string | undefined>;
export const appSchool: AppSchool | null = env.VITE_APP_SCHOOL_CODE
  ? {
      code: env.VITE_APP_SCHOOL_CODE,
      name: env.VITE_APP_SCHOOL_NAME ?? env.VITE_APP_SCHOOL_CODE,
      logo: env.VITE_APP_SCHOOL_LOGO ?? '/app-brand/logo.png',
      primary: env.VITE_APP_PRIMARY ?? '#0A2540',
    }
  : null;

/** The public website, for links people share (inside the app the page address is the phone itself). */
export const webUrl = (path = ''): string => {
  const base = isNativeApp ? (env.VITE_WEB_URL ?? 'https://ojpschools.com.ng') : window.location.origin;
  return `${base.replace(/\/+$/, '')}${path}`;
};

/** Open a web page outside the app's own screens: the in-app browser on the phone, a new tab on the website. */
export async function openExternal(url: string, onClose?: () => void): Promise<void> {
  if (!isNativeApp) {
    window.open(url, '_blank', 'noopener');
    return;
  }
  const { Browser } = await import('@capacitor/browser');
  if (onClose) {
    const h = await Browser.addListener('browserFinished', () => { void h.remove(); onClose(); });
  }
  await Browser.open({ url, presentationStyle: 'fullscreen' });
}

const toBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
  r.onerror = () => reject(r.error);
  r.readAsDataURL(blob);
});

/**
 * Save a file the app made or downloaded. The website downloads it; the phone saves it to the app's documents
 * and opens the share sheet, so it can be opened in a PDF/Excel app, sent on WhatsApp or saved to Files.
 */
export async function saveBlob(blob: Blob, name: string): Promise<void> {
  if (!isNativeApp) {
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 2000);
    return;
  }
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([import('@capacitor/filesystem'), import('@capacitor/share')]);
  const safe = name.replace(/[^\w.\- ]+/g, '_') || 'file';
  const saved = await Filesystem.writeFile({ path: safe, data: await toBase64(blob), directory: Directory.Cache, recursive: true });
  try {
    await Share.share({ title: safe, url: saved.uri, dialogTitle: 'Open or save ' + safe });
  } catch {
    /* the person closed the share sheet */
  }
}

/** Phone-only start-up: status bar colour, hide the splash, and the Android back button. */
export async function startNativeShell(goBack: () => boolean): Promise<void> {
  if (!isNativeApp) return;
  document.documentElement.classList.add('native-app');
  const [{ App }, { StatusBar, Style }, { SplashScreen }] = await Promise.all([
    import('@capacitor/app'), import('@capacitor/status-bar'), import('@capacitor/splash-screen'),
  ]);
  try {
    await StatusBar.setStyle({ style: Style.Light });   // dark icons on a light bar
    await StatusBar.setBackgroundColor({ color: '#FFFFFF' });   // older Android; 15+ draws edge to edge
  } catch { /* older Android */ }
  void SplashScreen.hide();
  // back: close the open dialog/menu, else go back a screen; on the first screen, leave the app
  void App.addListener('backButton', () => {
    if (closeTopOverlay()) return;
    if (!goBack()) void App.exitApp();
  });
}

/** Close the top-most dialog or slide-out menu the way a person would: its Close/Cancel button, a tap outside, or Escape. */
function closeTopOverlay(): boolean {
  const layers = Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"], .fixed.inset-0'))
    .filter((el) => el.offsetParent !== null || getComputedStyle(el).position === 'fixed')
    .filter((el) => getComputedStyle(el).display !== 'none' && getComputedStyle(el).visibility !== 'hidden');
  const top = layers[layers.length - 1];
  if (!top) return false;
  const close = Array.from(top.querySelectorAll<HTMLButtonElement>('button')).find((b) => {
    const t = `${b.getAttribute('aria-label') ?? ''} ${b.title ?? ''} ${b.textContent ?? ''}`.trim().toLowerCase();
    return /^(close|cancel|×|✕|back|not now)\b/.test(t) || /\bclose\b/.test(b.getAttribute('aria-label')?.toLowerCase() ?? '');
  });
  if (close) { close.click(); return true; }
  // backdrops close on a tap that lands on the backdrop itself
  top.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
  top.dispatchEvent(new MouseEvent('click', { bubbles: true }));
  const esc = new KeyboardEvent('keydown', { key: 'Escape', bubbles: true });
  (top.querySelector('form') ?? top).dispatchEvent(esc);
  document.dispatchEvent(esc);
  return true;
}
